function getGalleryImages(scrollable) {
  return [...scrollable.querySelectorAll('.wc-block-product-gallery-thumbnails__thumbnail__image')];
}

function isThumbnailVisible(image) {
  const thumb = image.closest('.wc-block-product-gallery-thumbnails__thumbnail') || image;

  return getComputedStyle(thumb).display !== 'none' && thumb.getClientRects().length > 0;
}

function getVisibleGalleryImages(scrollable) {
  return getGalleryImages(scrollable).filter(isThumbnailVisible);
}

function getActiveImageIndex(images) {
  const activeIndex = images.findIndex((image) =>
    image.classList.contains('wc-block-product-gallery-thumbnails__thumbnail__image--is-active')
  );

  return activeIndex >= 0 ? activeIndex : 0;
}

function getActiveImageId(scrollable) {
  const images = getGalleryImages(scrollable);
  const activeIndex = getActiveImageIndex(images);

  return images[activeIndex]?.getAttribute('data-image-id') ?? null;
}

function syncActiveLargeImage(gallery, scrollable) {
  const container = gallery.querySelector('.wc-block-product-gallery-large-image__container');

  if (!container) {
    return;
  }

  const activeImageId = getActiveImageId(scrollable);

  container.querySelectorAll('.wc-block-product-gallery-large-image__wrapper').forEach((wrapper) => {
    const imageId = wrapper.querySelector('img[data-image-id]')?.getAttribute('data-image-id');
    wrapper.classList.toggle('product-gallery-slide--active', Boolean(activeImageId && imageId === activeImageId));
  });

  gallery.classList.add('product-gallery-slides--synced');

  if (window.matchMedia('(min-width: 781px)').matches) {
    container.scrollLeft = 0;
    return;
  }

  const activeIndex = getActiveImageIndex(getGalleryImages(scrollable));
  container.scrollLeft = activeIndex * container.clientWidth;
}

let galleryResizeTimer;

function scheduleGalleryResizeSync() {
  window.clearTimeout(galleryResizeTimer);
  galleryResizeTimer = window.setTimeout(() => {
    document.querySelectorAll('.wp-block-woocommerce-product-gallery').forEach((gallery) => {
      const scrollable = gallery.querySelector('.wc-block-product-gallery-thumbnails__scrollable');

      if (scrollable) {
        syncActiveLargeImage(gallery, scrollable);
      }
    });
  }, 100);
}

function resetThumbnailAspectRatio(scrollable) {
  scrollable.querySelectorAll('.wc-block-product-gallery-thumbnails__thumbnail__image').forEach((image) => {
    image.style.removeProperty('aspect-ratio');
  });
}

function shouldShowGalleryControls(gallery, scrollable) {
  if (gallery.classList.contains('is-single-product-gallery-image')) {
    return false;
  }

  return getVisibleGalleryImages(scrollable).length > 1;
}

function bindGalleryControls(gallery, scrollable, controls, prevButton, nextButton) {
  const updateButtons = () => {
    const images = getVisibleGalleryImages(scrollable);
    const showArrows = shouldShowGalleryControls(gallery, scrollable);

    gallery.classList.toggle('product-gallery-thumbnails--has-controls', showArrows);
    controls.hidden = !showArrows;
    syncActiveLargeImage(gallery, scrollable);

    if (!showArrows) {
      return;
    }

    const activeIndex = getActiveImageIndex(images);
    prevButton.disabled = activeIndex <= 0;
    nextButton.disabled = activeIndex >= images.length - 1;
  };

  if (prevButton.dataset.bound !== 'true') {
    prevButton.dataset.bound = 'true';
    prevButton.addEventListener('click', () => {
      const images = getVisibleGalleryImages(scrollable);
      const activeIndex = getActiveImageIndex(images);

      if (activeIndex > 0) {
        images[activeIndex - 1].click();
      }
    });
  }

  if (nextButton.dataset.bound !== 'true') {
    nextButton.dataset.bound = 'true';
    nextButton.addEventListener('click', () => {
      const images = getVisibleGalleryImages(scrollable);
      const activeIndex = getActiveImageIndex(images);

      if (activeIndex < images.length - 1) {
        images[activeIndex + 1].click();
      }
    });
  }

  if (gallery.dataset.galleryBound !== 'true') {
    gallery.dataset.galleryBound = 'true';

    const observer = new MutationObserver(() => {
      resetThumbnailAspectRatio(scrollable);
      updateButtons();
    });
    observer.observe(scrollable, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      subtree: true,
      childList: true,
    });
  }

  resetThumbnailAspectRatio(scrollable);
  updateButtons();
}

function initProductGalleryThumbnails(root) {
  const scrollable = root.querySelector('.wc-block-product-gallery-thumbnails__scrollable');

  if (!scrollable) {
    return;
  }

  const gallery = root.closest('.wp-block-woocommerce-product-gallery');

  if (!gallery) {
    return;
  }

  root.querySelector('.product-gallery-thumbnails__controls')?.remove();

  let controls = gallery.querySelector('.product-gallery-thumbnails__controls');

  if (!controls) {
    controls = document.createElement('div');
    controls.className = 'product-gallery-thumbnails__controls';
    controls.hidden = true;

    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'product-gallery-thumbnails__arrow product-gallery-thumbnails__arrow--prev';
    prevButton.setAttribute('aria-label', 'Previous image');

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'product-gallery-thumbnails__arrow product-gallery-thumbnails__arrow--next';
    nextButton.setAttribute('aria-label', 'Next image');

    controls.appendChild(prevButton);
    controls.appendChild(nextButton);
    gallery.appendChild(controls);
  }

  const prevButton = controls.querySelector('.product-gallery-thumbnails__arrow--prev');
  const nextButton = controls.querySelector('.product-gallery-thumbnails__arrow--next');

  if (!prevButton || !nextButton) {
    return;
  }

  bindGalleryControls(gallery, scrollable, controls, prevButton, nextButton);
}

function initAllProductGalleryThumbnails(context = document) {
  context.querySelectorAll('.wc-block-product-gallery-thumbnails').forEach((root) => {
    initProductGalleryThumbnails(root);
  });
}

function bootProductGalleryThumbnails() {
  initAllProductGalleryThumbnails();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootProductGalleryThumbnails);
} else {
  bootProductGalleryThumbnails();
}

window.addEventListener('load', bootProductGalleryThumbnails);

if (!window.__productGalleryResizeBound) {
  window.__productGalleryResizeBound = true;
  window.addEventListener('resize', scheduleGalleryResizeSync);
}
